/**
 * Recipient Space-invite inbox facts: extra roles, extra channels, invite
 * expiry, and temporary membership. Counts only — invitees never receive names.
 *
 * `inviteRoleId` is ignored. That generated role is the channel-binding
 * implementation, not an assigned Space role.
 *
 * Pending rows with a future `expiresAt` use relative remaining time. Declined,
 * expired, and revoked rows use an absolute local datetime instead.
 *
 * Display order: invite expiry (or outcome), combined role/channel access,
 * temporary membership.
 */

import type { TFunction } from 'i18next';
import type { PublicSpaceInvite, SpaceTemporaryAccessDuration } from '@adieuu/shared';
import {
  formatSpaceInviteExpiryWhen,
  relativeExpiry,
} from '../../pages/spaces/spaceInviteUtils';

export type InviteInboxOutcomeFact =
  | { kind: 'declined'; at: string }
  | { kind: 'expired'; at: string }
  | { kind: 'revoked'; at: string };

export type SpaceInviteInboxFact =
  | { kind: 'access'; roleCount: number; channelCount: number }
  | {
      kind: 'inviteExpiry';
      rel: { kind: 'future'; unit: 'd' | 'h' | 'm'; value: number };
    }
  | InviteInboxOutcomeFact
  | { kind: 'temporaryMembership'; duration: SpaceTemporaryAccessDuration };

export type InviteInboxOutcomeSource = {
  status?: string;
  expiresAt?: string | null;
  updatedAt?: string;
};

export type SpaceInviteInboxFactSections = {
  expiry: string | null;
  access: string | null;
  temporaryMembership: string | null;
};

type SpaceInviteInboxSource = Pick<
  PublicSpaceInvite,
  'grantedRoleIds' | 'grantedChannelIds' | 'expiresAt' | 'temporaryAccess' | 'inviteRoleId'
> &
  InviteInboxOutcomeSource;

const EXPIRY_FACT_KINDS = new Set<SpaceInviteInboxFact['kind']>([
  'inviteExpiry',
  'declined',
  'expired',
  'revoked',
]);

function validIsoInstant(iso: string | null | undefined): string | null {
  if (typeof iso !== 'string' || iso.length === 0) return null;
  if (!Number.isFinite(new Date(iso).getTime())) return null;
  return iso;
}

function isTerminalInviteStatus(status: string | undefined): boolean {
  return (
    status === 'declined' ||
    status === 'revoked' ||
    status === 'expired' ||
    status === 'accepted'
  );
}

function inviteExpiryFact(
  expiresAt: string | null | undefined,
  now: Date,
): SpaceInviteInboxFact | null {
  if (typeof expiresAt !== 'string' || expiresAt.length === 0) return null;
  if (!Number.isFinite(new Date(expiresAt).getTime())) return null;
  const rel = relativeExpiry(expiresAt, now);
  if (rel.kind !== 'future') return null;
  return { kind: 'inviteExpiry', rel };
}

/**
 * Absolute declined / expired / revoked timestamp, or a lapsed pending
 * `expiresAt`. Relative remaining time is not an outcome.
 */
export function inviteInboxOutcomeFact(
  invite: InviteInboxOutcomeSource,
  now: Date = new Date(),
): InviteInboxOutcomeFact | null {
  if (invite.status === 'declined') {
    const at = validIsoInstant(invite.updatedAt);
    return at ? { kind: 'declined', at } : null;
  }
  if (invite.status === 'revoked') {
    const at = validIsoInstant(invite.updatedAt);
    return at ? { kind: 'revoked', at } : null;
  }
  if (invite.status === 'expired') {
    const at = validIsoInstant(invite.expiresAt) ?? validIsoInstant(invite.updatedAt);
    return at ? { kind: 'expired', at } : null;
  }
  if (invite.status === 'accepted') return null;

  if (typeof invite.expiresAt === 'string') {
    const rel = relativeExpiry(invite.expiresAt, now);
    if (rel.kind === 'past') {
      const at = validIsoInstant(invite.expiresAt);
      return at ? { kind: 'expired', at } : null;
    }
  }

  return null;
}

/**
 * Optional grant/expiry facts in display order: invite expiry or outcome,
 * combined access, temporary membership. Absent fields are omitted.
 */
export function spaceInviteInboxFacts(
  invite: SpaceInviteInboxSource,
  now: Date = new Date(),
): SpaceInviteInboxFact[] {
  const facts: SpaceInviteInboxFact[] = [];

  const outcome = inviteInboxOutcomeFact(invite, now);
  if (outcome) {
    facts.push(outcome);
  } else if (!isTerminalInviteStatus(invite.status)) {
    const expiry = inviteExpiryFact(invite.expiresAt, now);
    if (expiry) facts.push(expiry);
  }

  const roleCount = invite.grantedRoleIds?.length ?? 0;
  const channelCount = invite.grantedChannelIds?.length ?? 0;
  if (roleCount > 0 || channelCount > 0) {
    facts.push({ kind: 'access', roleCount, channelCount });
  }

  if (invite.temporaryAccess?.kind === 'duration') {
    facts.push({ kind: 'temporaryMembership', duration: invite.temporaryAccess.duration });
  }

  return facts;
}

function outcomeFactLabel(fact: InviteInboxOutcomeFact, t: TFunction): string {
  const when = formatSpaceInviteExpiryWhen(fact.at);
  if (!when) return '';
  return t(`invitationInbox.${fact.kind}At`, { when });
}

function accessFactLabel(roleCount: number, channelCount: number, t: TFunction): string {
  if (roleCount > 0 && channelCount > 0) {
    return t('spaces.invites.inbox.includesAccess', {
      roles: t('spaces.invites.inbox.roleCount', { count: roleCount }),
      channels: t('spaces.invites.inbox.channelCount', { count: channelCount }),
    });
  }
  if (roleCount > 0) {
    return t('spaces.invites.inbox.includesAccessRoles', { count: roleCount });
  }
  return t('spaces.invites.inbox.includesAccessChannels', { count: channelCount });
}

function labelledFact(
  facts: SpaceInviteInboxFact[],
  match: SpaceInviteInboxFact['kind'] | ReadonlySet<SpaceInviteInboxFact['kind']>,
  t: TFunction,
): string | null {
  const fact =
    typeof match === 'string'
      ? facts.find((entry) => entry.kind === match)
      : facts.find((entry) => match.has(entry.kind));
  if (!fact) return null;
  const label = spaceInviteInboxFactLabel(fact, t);
  return label.length > 0 ? label : null;
}

/** Localized labels grouped for the list row and hover card. */
export function spaceInviteInboxFactSections(
  invite: SpaceInviteInboxSource,
  t: TFunction,
  now: Date = new Date(),
): SpaceInviteInboxFactSections {
  const facts = spaceInviteInboxFacts(invite, now);
  return {
    expiry: labelledFact(facts, EXPIRY_FACT_KINDS, t),
    access: labelledFact(facts, 'access', t),
    temporaryMembership: labelledFact(facts, 'temporaryMembership', t),
  };
}

/** Localized label for one inbox fact. Shared by the list row and hover card. */
export function spaceInviteInboxFactLabel(fact: SpaceInviteInboxFact, t: TFunction): string {
  switch (fact.kind) {
    case 'access':
      return accessFactLabel(fact.roleCount, fact.channelCount, t);
    case 'inviteExpiry':
      return t('spaces.invites.inbox.inviteExpires', {
        when: t(`spaces.invites.inbox.expiresIn.${fact.rel.unit}`, { count: fact.rel.value }),
      });
    case 'declined':
    case 'expired':
    case 'revoked':
      return outcomeFactLabel(fact, t);
    case 'temporaryMembership':
      return t('spaces.invites.inbox.temporaryMembership', {
        duration: t(`spaces.invites.duration.${fact.duration}`),
      });
  }
}
